// src/components/PhotoCarousel.jsx
import React, { useState, useEffect, useCallback } from 'react';

/**
 * Componente interactivo de Carrusel Fotográfico
 * Muestra imágenes oficiales de la edición anterior de Xolo Fest.
 * Soporta navegación previa/siguiente, gestos táctiles (swipe) en móviles,
 * precarga inteligente de la imagen siguiente y auto-avance pausible.
 */
export default function PhotoCarousel({
  images = ['/img/carrousel/xolofest_xolos_interaktia_1.jpg'],
  autoPlayInterval = 5000
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);

  const total = images.length;

  const handleNext = useCallback(() => {
    if (total <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % total);
  }, [total]);

  const handlePrev = useCallback(() => {
    if (total <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  // Autoplay con temporizador
  useEffect(() => {
    if (total <= 1 || isPaused) return;
    const timer = setInterval(() => {
      handleNext();
    }, autoPlayInterval);

    return () => clearInterval(timer);
  }, [total, isPaused, autoPlayInterval, handleNext]);

  // Precarga anticipada de la siguiente imagen para transición instantánea
  useEffect(() => {
    if (total > 1) {
      const nextIndex = (currentIndex + 1) % total;
      const preloaded = new Image();
      preloaded.src = images[nextIndex];
    }
  }, [currentIndex, images, total]);

  // Soporte de gestos táctiles en pantallas móviles (Swipe izquierda / derecha)
  const handleTouchStart = (e) => {
    if (e.touches && e.touches.length === 1) {
      setTouchStartX(e.touches[0].clientX);
      setIsPaused(true);
    }
  };

  const handleTouchEnd = (e) => {
    if (touchStartX !== null && e.changedTouches && e.changedTouches.length === 1) {
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartX - touchEndX;
      if (diff > 45) {
        handleNext();
      } else if (diff < -45) {
        handlePrev();
      }
    }
    setTouchStartX(null);
    setIsPaused(false);
  };

  return (
    <div
      className="carousel-frame position-relative mx-auto"
      style={{ maxWidth: '980px', userSelect: 'none' }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Imagen Activa */}
      <img
        src={images[currentIndex]}
        alt={`Xolo Fest Edición Anterior - Foto ${currentIndex + 1}`}
        className="carousel-img-item"
        loading="eager"
      />

      {/* Sombra y Badge conmemorativo inferior */}
      <div
        className="position-absolute bottom-0 start-0 w-100 p-3 p-md-4 d-flex justify-content-between align-items-end"
        style={{
          background: 'linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0) 100%)',
          pointerEvents: 'none'
        }}
      >
        <div>
          <span className="badge bg-danger text-white fw-bold px-3 py-1 mb-1 text-uppercase letter-spacing-md shadow-sm">
            Edición Anterior
          </span>
          <p className="text-white small mb-0 d-none d-sm-block" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
            Estadio Caliente · La fiesta rojinegra más grande de Tijuana
          </p>
        </div>

        {total > 1 && (
          <span className="badge bg-black bg-opacity-80 text-white border border-secondary border-opacity-50 px-3 py-1 small fw-bold shadow">
            {currentIndex + 1} / {total}
          </span>
        )}
      </div>

      {/* Flechas de navegación (visibles si hay más de 1 imagen) */}
      {total > 1 && (
        <>
          <button
            type="button"
            className="carousel-nav-btn carousel-nav-prev"
            onClick={handlePrev}
            aria-label="Foto anterior"
          >
            <i className="bi bi-chevron-left"></i>
          </button>
          <button
            type="button"
            className="carousel-nav-btn carousel-nav-next"
            onClick={handleNext}
            aria-label="Foto siguiente"
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </>
      )}

      {/* Indicadores de navegación */}
      {/* Para galerías pequeñas (<= 15 fotos): Puntos tradicionales */}
      {total > 1 && total <= 15 && (
        <div className="carousel-indicators-custom">
          {images.map((_, idx) => (
            <button
              key={idx}
              type="button"
              className={`carousel-dot ${idx === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(idx)}
              aria-label={`Ir a foto ${idx + 1}`}
            />
          ))}
        </div>
      )}

      {/* Para galerías masivas (> 15 fotos como las 96 oficiales): Barra de progreso rojinegra estilizada */}
      {total > 15 && (
        <div
          className="position-absolute bottom-0 start-50 translate-middle-x mb-2"
          style={{ width: '45%', maxWidth: '240px', zIndex: 5 }}
        >
          <div
            className="w-100 rounded-pill overflow-hidden shadow-sm"
            style={{ height: '4px', backgroundColor: 'rgba(255, 255, 255, 0.25)' }}
          >
            <div
              className="h-100 bg-danger"
              style={{
                width: `${((currentIndex + 1) / total) * 100}%`,
                transition: 'width 0.3s ease'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
